import React, { useEffect, useMemo, useState } from 'react';
import { AdjustmentsHorizontalIcon, ArrowDownTrayIcon, CircleStackIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ButtonLinkList from '@ui/button/ButtonLinkList';
import FormButton from '@ui/button/FormButton';
import ToolbarButton from '@ui/button/ToolbarButton';
import CaptionGroup, { CaptionGroupDivider } from '@ui/captiongroup/CaptionGroup';
import ChartCard from '@ui/chart/ChartCard';
import DataTable, { DataTableRow, DataTableSort } from '@ui/chart/DataTable';
import KpiStrip, { KpiStripItem } from '@ui/chart/KpiStrip';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import ColumnsPanel, { ColumnsPanelItem } from '@ui/menu/ColumnsPanel';
import Message from '@ui/message/Message';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import SelectionBar from '@ui/selection/SelectionBar';
import DateRangePicker, { DateRangePreset } from '@ui/selector/DateRangePicker';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import ToggleChip from '@ui/tag/ToggleChip';
import { DailyPerformanceExportInput, StlyBasis } from '@app/model/bi/Bi';
import { useBiColumns, useDailyCompset, useDailyPerformance, useSaveBiColumns } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import DailyDayCard from '../fragment/DailyDayCard';
import BiSwitchRow from '../fragment/BiSwitchRow';
import {
	CompsetChoice, DEFAULT_COLUMNS, MAX_RANGE_MONTHS, MONTH_LONG, NightRange, PHONE_COLUMNS, RANGE_PICKS, RangePickId, WEEKDAY_DAYS, WEEKEND_DAYS, dailyColumns,
	dayOf, exportQuery, figuresByNight, isRangePick, leadOf, monthLabel, monthNote, nightLabel, nightRow, nightsOf, overCapacity, panelItems, pickRange,
	rangeLimits, scanSource, sumOf, tableColumns, totalCells, validRange, wholeMonth,
} from '../fragment/DailyPerformance.utilities';
import { dailyFileName, formatDate, formatDateTime, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';

// ROO2-288: the Daily Performance report (BI report 6, mock/bi-daily-performance): one property, one row per night of a
// range of 1 night to 12 months (ROO2-298), today against the situations D-1 … D-30 and last year. Read-only: no control
// changes a price.

// ROO2-298 D52: the browser keeps the last quick pick (localStorage); a custom range lasts for the browser session only
// (sessionStorage), and a new session opens the last quick pick again. The first visit opens This month. A read or a write
// can fail (a private window): then the page uses This month. The old key roompulse.bi.daily.window is not read (E3).
const PICK_KEY = 'roompulse.bi.daily.range';
const CUSTOM_KEY = 'roompulse.bi.daily.customRange';
type RangeChoice = { pick: RangePickId } | { custom: NightRange };
const readChoice = (): RangeChoice => {
	try {
		const custom = JSON.parse(window.sessionStorage.getItem(CUSTOM_KEY) ?? 'null') as NightRange | null;
		if (custom !== null && typeof custom.from === 'string' && typeof custom.to === 'string' && validRange(custom)) return { custom: { from: custom.from, to: custom.to } };
	} catch { /* no custom range */ }
	try {
		const pick = window.localStorage.getItem(PICK_KEY);
		return { pick: isRangePick(pick) ? pick : 'thisMonth' };
	} catch {
		return { pick: 'thisMonth' };
	}
};
const storeChoice = (choice: RangeChoice) => {
	try {
		if ('pick' in choice) {
			window.localStorage.setItem(PICK_KEY, choice.pick);
			window.sessionStorage.removeItem(CUSTOM_KEY);
		} else window.sessionStorage.setItem(CUSTOM_KEY, JSON.stringify(choice.custom));
	} catch { /* the choice lives for this page only */ }
};
// ROO2-289 D21: this browser keeps the compset choice (Median | W. avg) once the user picks one.
const COMPSET_KEY = 'roompulse.bi.daily.compset';
const readCompset = (): CompsetChoice | null => {
	try {
		const value = window.localStorage.getItem(COMPSET_KEY);
		return value === 'MEDIAN' || value === 'WEIGHTED_AVERAGE' ? value : null;
	} catch {
		return null;
	}
};
const storeCompset = (value: CompsetChoice) => {
	try { window.localStorage.setItem(COMPSET_KEY, value); } catch { /* the choice lives for this page only */ }
};
const fmtEur = (v: number | null) => v === null ? '–' : `€${Math.round(v).toLocaleString('en-GB')}`;
const fmtEur2 = (v: number | null) => v === null ? '–' : `€${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v: number | null) => v === null ? '–' : `${(v * 100).toFixed(1)}%`;
// "1 night", "0 nights", "58 nights" (ROO2-298: a range can hold one night).
const nightsLabel = (count: number) => `${count} night${count === 1 ? '' : 's'}`;
const ratio = (a: number | null, b: number | null) => a === null || b === null || b === 0 ? null : a / b;

// D33: a phone (under the sm breakpoint, 640 px) gets the phone columns and the stacked controls.
const usePhone = () => {
	const query = '(max-width: 639px)';
	const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
	useEffect(() => {
		const media = window.matchMedia(query);
		const onChange = () => setPhone(media.matches);
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, []);
	return phone;
};

// RQ-12: the shown days of the week (0 = Sunday) and whether the past nights are hidden.
type Filter = { days: number[], future: boolean };
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 0];
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const BiDailyPerformancePage = () => {
	const width = useBiWidthClass();
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const phone = usePhone();
	const toaster = useToaster();
	const s = scope.data;
	// D11: one property at a time; All properties or a Property group opens the first property with reservations (RQ-03).
	const property = address.property ?? s?.properties.find(p => p.reservationsSource !== null) ?? null;
	const hotelId = property?.companyHotelId ?? null;
	// The as-of day of the cuts and the hotel's first stay night come with the night figures; until they load, the scope's
	// day and no lower limit.
	const [asOfSeen, setAsOfSeen] = useState<string | null>(null);
	const [firstSeen, setFirstSeen] = useState<string | null>(null);
	const asOf = asOfSeen ?? s?.asOf ?? new Date().toISOString().slice(0, 10);
	const [rangeChoice, setRangeChoice] = useState<RangeChoice>(readChoice);
	// A quick pick is relative to the as-of day; the button shows its name while the range equals it.
	const presets: DateRangePreset[] = RANGE_PICKS.map(p => ({ id: p.id, label: p.label, range: pickRange(p.id, asOf) }));
	const range: NightRange = 'pick' in rangeChoice ? pickRange(rangeChoice.pick, asOf) : rangeChoice.custom;
	const limits = rangeLimits(asOf, firstSeen);
	// E2: a pick on the days that equals a quick pick counts as that quick pick.
	const setRange = (next: NightRange) => {
		const preset = presets.find(p => p.range.from === next.from && p.range.to === next.to);
		const choice: RangeChoice = preset === undefined ? { custom: next } : { pick: preset.id as RangePickId };
		setRangeChoice(choice);
		storeChoice(choice);
	};
	const [stly, setStly] = useState<StlyBasis>('WEEKDAY');
	const [filter, setFilter] = useState<Filter>({ days: ALL_DAYS, future: false });
	const [selected, setSelected] = useState<string[]>([]);
	const [sort, setSort] = useState<DataTableSort | undefined>(undefined);
	const [exporting, setExporting] = useState(false);
	const report = useDailyPerformance(hotelId, range.from, range.to, stly);
	const compset = useDailyCompset(hotelId, range.from, range.to);
	const [pickedCompset, setPickedCompset] = useState<CompsetChoice | null>(readCompset);
	const savedColumns = useBiColumns('DAILY');
	const saveColumns = useSaveBiColumns('DAILY');
	const columnsDrawer = useDrawer();

	// A new property or range clears the selection, and the table opens again in night order (the key of the table).
	useEffect(() => { setSelected([]); setSort(undefined); }, [hotelId, range.from, range.to]);
	useEffect(() => {
		if (report.data === undefined) return;
		if (report.data.asOf !== asOfSeen) setAsOfSeen(report.data.asOf);
		if (report.data.firstStayNight !== firstSeen) setFirstSeen(report.data.firstStayNight);
	}, [report.data, asOfSeen, firstSeen]);

	const data = report.data;
	const rooms = data?.rooms ?? null;
	const imported = data?.importedAt === null || data?.importedAt === undefined ? '–' : formatDateTime(data.importedAt);
	const comp = compset.data;
	// D20, D21: the hotel's Comparison Method until the user picks; W. avg only with competitor importance.
	const compChoice: CompsetChoice = comp?.weightedAverageAvailable === true ? (pickedCompset ?? comp.comparisonMethod) : 'MEDIAN';
	const setCompset = (value: string) => { setPickedCompset(value as CompsetChoice); storeCompset(value as CompsetChoice); };
	const compsetLabel = compChoice === 'WEIGHTED_AVERAGE' ? 'vs W. avg' : 'vs Median';
	const scanNights = comp?.nights ?? 1;
	const compsetTabs = <ButtonTabs label="Compset value" value={compChoice} onChange={setCompset}
		options={[{ value: 'MEDIAN', label: 'Median' }, { value: 'WEIGHTED_AVERAGE', label: 'W. avg', disabled: comp?.weightedAverageAvailable !== true }]} />;
	const allColumns = useMemo(() => dailyColumns(rooms, compsetLabel, scanNights), [rooms, compsetLabel, scanNights]);
	const defaults = phone ? PHONE_COLUMNS : DEFAULT_COLUMNS;
	// D22: the user account keeps the order and the visibility of the columns; a phone shows its own set until the user saves one.
	const items = useMemo(() => panelItems(allColumns, savedColumns.data?.columns ?? null, defaults), [allColumns, savedColumns.data, defaults]);
	const { columns, groups } = useMemo(() => tableColumns(allColumns, items, phone), [allColumns, items, phone]);
	const compsetDays = useMemo(() => new Map((comp?.days ?? []).map(d => [d.date, d])), [comp]);
	// G8: a pointer on the chip of a future night opens the RoomPulse day popup of that night (a tap on a phone). A month row,
	// a past night and a night with no chip have no card.
	const tableCols = useMemo(() => columns.map(c => c.id !== 'diff' || hotelId === null ? c : {
		...c,
		hoverCard: (night: string) => night.length !== 10 || leadOf(night, asOf) < 0 || compsetDays.get(night)?.status !== 'OK' ? null
			: <DailyDayCard companyHotelId={hotelId} night={night} label={nightLabel(night)} day={compsetDays.get(night)} choice={compChoice} nights={scanNights} currency={comp?.currency ?? 'EUR'} compact={phone} />,
		hoverCardLabel: (night: string) => `RoomPulse day popup of ${nightLabel(night)}`,
	}), [columns, compsetDays, asOf, hotelId, compChoice, phone]);
	const byNight = useMemo(() => data === undefined ? undefined : figuresByNight(data, address.revenueScope, address.vat), [data, address.revenueScope, address.vat]);
	const nights = nightsOf(range.from, range.to);
	const shown = nights.filter(n => filter.days.includes(dayOf(n)) && (!filter.future || leadOf(n, asOf) >= 0));
	const filtered = shown.length !== nights.length;

	if (scope.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="Daily Performance did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (s === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;
	if (property === null) return <>
		<BiPageHeader scope={s} companyHotelId={null} search={address.search} sublabel="Daily performance" page="Daily performance" propertiesOnly fullWidthSwitch />
		<main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
			<Message state="info" title="No reservations yet" flush>Daily performance needs a property with a Reservations integration.</Message>
		</main>
	</>;

	const sum = byNight === undefined ? undefined : sumOf(nights, byNight, asOf, rooms);
	const sumShown = byNight === undefined ? undefined : sumOf(shown, byNight, asOf, rooms);
	const monthsOfWindow = [...new Set(nights.map(n => n.slice(0, 7)))];
	const rowsOf = (list: string[]): DataTableRow[] => byNight === undefined ? [] : list.flatMap(n => {
		const f = byNight.get(n);
		return f === undefined ? [] : [nightRow(n, f, rooms, asOf, phone, { day: compsetDays.get(n), choice: compChoice })];
	});
	// D29, D51: a range over more than one month has a row per month (the sum of its shown nights) with its nights below; a
	// month at an end of the range holds only its nights in the range.
	const rows: DataTableRow[] = byNight === undefined ? [] : monthsOfWindow.length === 1
		? rowsOf(shown)
		: monthsOfWindow.map(m => {
			const inMonth = shown.filter(n => n.startsWith(m));
			const all = nights.filter(n => n.startsWith(m));
			const label = `${MONTH_LONG[Number(m.slice(5, 7)) - 1]}${inMonth.length < all.length ? ' *' : ''}`;
			return { id: m, cells: totalCells(label, sumOf(inMonth, byNight, asOf, rooms), rooms), note: monthNote(all, inMonth.length), children: rowsOf(inMonth) };
		}).filter(r => (r.children ?? []).length > 0);
	// ROO2-298: one whole calendar month keeps the month names ("October 2026", "October total"); any other range names its nights.
	const oneMonth = wholeMonth(range);
	const nightsWord = nightsLabel(nights.length);
	const footerLabel = oneMonth ? `${MONTH_LONG[Number(range.from.slice(5, 7)) - 1]} total${filtered ? ' *' : ''}` : `Total · ${nightsLabel(shown.length)}${filtered ? ' *' : ''}`;
	const empty = sum !== undefined && sum.cuts.D0.rn === 0 && sum.cuts.LY_ACTUAL.rn === 0;
	const over = byNight === undefined ? [] : overCapacity(nights, byNight, rooms);
	const state = report.isError ? 'error' as const : (data === undefined ? 'loading' as const : (report.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const name = shortName(property, s.companyName);
	// The selection keeps a night that a filter hides; the bar sums only the shown ones.
	const pickedNights = selected.filter(n => shown.includes(n));
	const chosen = byNight === undefined ? undefined : sumOf(pickedNights, byNight, asOf, rooms);
	const occ = (x: { rn: number }, n: number) => ratio(x.rn, rooms === null ? null : rooms * n);

	const kpis: KpiStripItem[] = sum === undefined ? [] : (() => {
		const t = sum.cuts.D0, ly = sum.cuts.STLY, n = sum.nights;
		// D40: a window with a night that has no last-year data has no change vs STLY.
		const pct = (a: number | null, b: number | null) => sum.lyMissing || a === null || b === null || b === 0 ? undefined : { value: a / b - 1, format: 'percent' as const, label: 'vs STLY' };
		const o = occ(t, n), oLy = sum.lyMissing ? null : occ(ly, n);
		const revpar = (rev: number | null) => ratio(rev, rooms === null ? null : rooms * n);
		return [
			{ id: 'occ', label: 'Occupancy', value: o, format: 'percent', decimals: 1, change: o !== null && oLy !== null ? { value: o - oLy, format: 'points', label: 'vs STLY' } : undefined },
			{ id: 'rn', label: 'Room nights', value: t.rn, format: 'number', change: pct(t.rn, ly.rn) },
			{ id: 'rev', label: 'Revenue', value: t.rev, format: 'money', change: pct(t.rev, ly.rev) },
			{ id: 'adr', label: 'ADR', value: ratio(t.rev, t.rn), format: 'money', decimals: 2, change: pct(ratio(t.rev, t.rn), ratio(ly.rev, ly.rn)) },
			{ id: 'revpar', label: 'RevPAR', value: revpar(t.rev), format: 'money', decimals: 2, change: pct(revpar(t.rev), revpar(ly.rev)) },
		];
	})();

	const toggleDay = (d: number) => setFilter(f => ({ ...f, days: f.days.includes(d) ? f.days.filter(x => x !== d) : [...f.days, d] }));
	const onColumns = (next: ColumnsPanelItem[]) => saveColumns.mutate(next.map(i => ({ id: i.id, visible: i.visible })),
		{ onError: () => toaster.error('The columns were not saved', 'Try again. The table keeps your change for this visit.') });

	// B11: the Excel file. Current view = the table as it shows (the columns, the filters, the sort; a range over more than one
	// month lists the nights of its first month, the month that the table opens, E4); All data = every column of every night.
	const exportReady = data !== undefined && !report.isPlaceholderData && !report.isError && !exporting;
	const exportFile = (file: DailyPerformanceExportInput['file']) => {
		if (!exportReady) return;
		setExporting(true);
		const view = exportQuery({ columns: columns.map(c => c.id), days: filter.days, future: filter.future, sort,
			open: monthsOfWindow.length > 1 ? monthsOfWindow.slice(0, 1) : [] });
		BiService.downloadDailyPerformanceExcel({ companyHotelId: property.companyHotelId, stayFrom: range.from, stayTo: range.to, stlyBasis: stly, file,
			scope: address.revenueScope, vat: address.vat, ...view })
			.then(blob => saveFile(blob, dailyFileName(name, file === 'ALL_DATA' ? 'All data' : 'Current view',
				{ from: range.from.slice(0, 7), to: range.to.slice(0, 7) }, asOf)))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};

	// D28, D34: the sums of the selected nights. Above the table on a wide screen; on a phone after the night table, so
	// that it stays at the bottom of the screen while the user scrolls the nights (sticky).
	const selectionBar = chosen !== undefined && pickedNights.length > 0
		? <SelectionBar title={`${pickedNights.length} night${pickedNights.length === 1 ? '' : 's'} selected`} onClear={() => setSelected([])} items={[
			{ id: 'occ', label: 'Occ', value: fmtPct(occ(chosen.cuts.D0, pickedNights.length)) }, { id: 'rn', label: 'RN', value: String(chosen.cuts.D0.rn) },
			{ id: 'rev', label: 'Revenue', value: fmtEur(chosen.cuts.D0.rev) }, { id: 'adr', label: 'ADR', value: fmtEur2(ratio(chosen.cuts.D0.rev, chosen.cuts.D0.rn)) }]} />
		: null;

	return <>
		{/* D24 of ROO2-277: "Reservations as of" sits in the line under the title, on its own line below 640 px; the right of the
			header holds only the Full width switch. */}
		<BiPageHeader scope={s} companyHotelId={hotelId} search={address.search} page="Daily performance" propertiesOnly fullWidthSwitch
			sublabel={`${property.area} · ${rooms ?? '–'} rooms · Daily performance${phone ? '' : ` · Reservations as of ${imported}`}`}>
			{phone && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{imported}</span></span>}
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			<BiSwitchRow scope={address.revenueScope} vat={address.vat}
				onScope={v => address.set({ revenue: v === 'TOTAL' ? 'total' : 'room' })} onVat={v => address.set({ vat: v === 'NET' ? 'net' : 'incl' })}
				line={data === undefined ? null : `${name} · ${nightsWord} · ${rooms ?? '–'} rooms`}
				sources={property.reservationsSource === null ? [] : [property.reservationsSource]}
				after={<><CaptionGroup caption="STLY" className="col-span-2 w-full sm:col-span-1 sm:w-auto"><div className="w-full min-w-0">
					<ButtonTabs label="Same time last year" value={stly} onChange={v => setStly(v as StlyBasis)}
						options={[{ value: 'WEEKDAY', label: 'Day of week' }, { value: 'DATE', label: 'Same date' }]} /></div></CaptionGroup>
					{/* D20: on a phone the compset choice sits with the controls; on a wide screen in the header of the table. */}
					{phone && <CaptionGroup caption="Compset" className="col-span-2 w-full"><div className="w-full min-w-0">{compsetTabs}</div></CaptionGroup>}</>}>
				{/* ROO2-298 D47: one Nights range (From – To, with quick picks) replaces Month and Show. */}
				<CaptionGroup caption="Nights" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
					<div className="flex w-full min-w-0 items-center">
						<DateRangePicker value={range} onChange={setRange} presets={presets} min={limits.min} max={limits.max} maxMonths={MAX_RANGE_MONTHS}
							today={asOf} variant="bare" label="Nights of the page" />
					</div>
				</CaptionGroup>
			</BiSwitchRow>
			{over.length > 0 && <Message state="warning" flush title={`${over.length} night${over.length === 1 ? ' sells' : 's sell'} more rooms than the property has (${rooms})`}>
				Occupancy reads above 100 % on {over.slice(0, 10).map(n => nightLabel(n)).join(', ')}{over.length > 10 ? ' …' : ''}.</Message>}
			{(data?.roomTypesWithoutCount.length ?? 0) > 0 && <Message state="warning" flush title="A room type has no room count">
				{data?.roomTypesWithoutCount.join(', ')} sold nights in this window but {data?.roomTypesWithoutCount.length === 1 ? 'has' : 'have'} no room count,
				so occupancy uses the other rooms only. Add the count in the room list of the Reservations integration.</Message>}
			{rooms === null && data !== undefined && <Message state="warning" flush title="No room count">Occupancy, RevPAR and rooms left need the room counts of the Reservations integration.</Message>}
			{/* No headline figures when the nights fail: the card below says so once. */}
			{state !== 'error' && <div className="flex flex-col gap-1"><KpiStrip ariaLabel={`${name}: headline figures`} items={kpis} state={state === 'loading' ? 'loading' : 'default'} />
				{kpis.length > 0 && <p className="text-xs text-gray-500">Against STLY ({stly === 'WEEKDAY' ? 'the same weekday 364 days earlier' : 'the same date last year'}).
					{sum?.lyMissing === true && data?.firstStayNight != null && <> No last-year data before {formatDate(data.firstStayNight)}: STLY and Final show –.</>}</p>}</div>}
			{!phone && selectionBar}
			{/* A phone: the header above names the hotel, so the card title is the nights only. */}
			<ChartCard title={`${phone ? '' : `${name} · `}${oneMonth ? monthLabel(range.from.slice(0, 7)) : nightsWord}`}
				period={`Nights ${nightLabel(range.from)} – ${nightLabel(range.to)} ${range.to.slice(0, 4)} · past nights actual, later nights on the books`}
				state={empty ? 'empty' : state} emptyLabel="Nothing on the books for these nights, this year or last year." onRetry={() => { void report.refetch(); }}
				errorLabel="The nights did not load."
				actions={<>
					{!phone && compsetTabs}
					<ButtonLinkList toolbar icon={<ArrowDownTrayIcon className="h-4 w-4" />} description={`Excel · ${name} · ${nightsWord}`}
						items={[
							{ name: 'Current view', detail: 'The table as it shows now', icon: <TableCellsIcon className="h-4 w-4" />, onClick: () => exportFile('CURRENT_VIEW') },
							{ name: 'All data', detail: 'Every column, the whole window and the month total', icon: <CircleStackIcon className="h-4 w-4" />, onClick: () => exportFile('ALL_DATA') },
						]}>{exporting ? 'Preparing…' : 'Export'}</ButtonLinkList>
					<ToolbarButton icon={<AdjustmentsHorizontalIcon className="h-4 w-4" />} onClick={() => columnsDrawer.open()}>Columns<span className="font-normal text-gray-500">· {columns.length}</span></ToolbarButton>
				</>}
				caption={<>Rev = {address.revenueScope === 'TOTAL' ? 'total' : 'room only'} revenue {address.vat === 'NET' ? 'without VAT' : 'incl. VAT'}. STLY = {stly === 'WEEKDAY' ? 'the same weekday 364 days earlier, as it stood 364 days ago' : 'the same date last year, as it stood one year ago'}; Final = that night as it stands today. PU n = today − situation D-n; ADR Δ = ADR today − ADR at D-n. {compsetLabel} = your lowest price ÷ the compset {compChoice === 'WEIGHTED_AVERAGE' ? 'weighted average' : 'median'} − 1, from the RoomPulse scan of {scanSource(scanNights)}; a past night keeps its last scan.{comp?.weightedAverageAvailable === true ? '' : ' W. avg is off: this hotel has no competitor importance in RoomPulse.'}</>}>
				<div className="flex flex-col gap-3">
					<div className="flex flex-wrap items-center gap-2">
						{/* RQ-12: a chip is a set of nights on the table; a struck chip hides them. Every chip is on at each visit. */}
						{ALL_DAYS.map((d, i) => <ToggleChip key={d} pressed={filter.days.includes(d)} onToggle={() => toggleDay(d)} title={`${WEEK[i]} nights`}>{WEEK[i]}</ToggleChip>)}
						<CaptionGroupDivider className="hidden sm:block" />
						<ToggleChip pressed={!filter.future} onToggle={on => setFilter(f => ({ ...f, future: !on }))} title="The nights before today">Past nights</ToggleChip>
						<span className="inline-flex items-center gap-x-3">
							<FormButton state="text" isLink size="small" onClick={() => setFilter(f => ({ ...f, days: WEEKDAY_DAYS }))}>Only weekdays</FormButton>
							<FormButton state="text" isLink size="small" onClick={() => setFilter(f => ({ ...f, days: WEEKEND_DAYS }))}>Only weekends</FormButton>
						</span>
						{filtered && <span className="inline-flex items-center gap-x-1 text-xs text-gray-500">{shown.length} of {nightsWord} ·
							<FormButton state="text" isLink size="small" onClick={() => setFilter({ days: ALL_DAYS, future: false })}>Show all</FormButton></span>}
					</div>
					{shown.length === 0
						? <p className="pb-4 text-sm text-gray-500">No nights match these filters.</p>
						// D29, D51: a range over more than one month shows a row per month; the first month opens, the others open on a
						// click. A new range starts again from that default (the key).
						: <DataTable key={`${hotelId}-${range.from}-${range.to}`} ariaLabel="Nights" framed={false} stickyHeader columns={tableCols} columnGroups={groups} rows={rows} sortCycle="threeClicks"
							sortScope={monthsOfWindow.length > 1 ? 'children' : 'top'} defaultExpanded={monthsOfWindow.slice(0, 1)} onSortChange={next => setSort(next)}
							selectedRowIds={selected} onRowsSelect={setSelected} density={phone ? 'compact' : 'comfortable'}
							footer={sumShown === undefined ? undefined : { id: 'total', cells: totalCells(footerLabel, sumShown, rooms) }} />}
					{filtered && <p className="text-xs text-gray-500">* Only the {nightsLabel(shown.length)} shown. The month total below covers all {nightsWord}.</p>}
				</div>
			</ChartCard>
			{phone && selectionBar !== null && <div className="sticky bottom-3 z-20">{selectionBar}</div>}
			{sum !== undefined && !empty && <ChartCard title={oneMonth ? `Month total · ${monthLabel(range.from.slice(0, 7))}` : `Total for ${nightsWord}`}
				period={`${nightsWord} · ${rooms === null ? '–' : sum.left.toLocaleString('en-GB')} rooms left over the ${sum.future} future night${sum.future === 1 ? '' : 's'}`}
				caption="Always the whole window, whatever filters are on. Δ Rev = revenue today ÷ that revenue − 1.">
				<div className="grid grid-cols-1 items-start gap-4 pb-1 lg:grid-cols-2">
					{/* A phone: compact cells, and no ADR here (the headline figures above hold it). */}
					<DataTable ariaLabel="Against last year" density={phone ? 'compact' : 'comfortable'} columns={[{ id: 'what', label: phone ? 'vs last year' : 'Against last year', sortable: false }, { id: 'rn', label: 'RN', format: 'number', sortable: false },
						{ id: 'rev', label: 'Revenue', format: 'money', sortable: false }, { id: 'd', label: 'Δ Rev', format: 'percent', decimals: 1, signed: true, tone: 'change', sortable: false },
						...(phone ? [] : [{ id: 'adr', label: 'ADR', format: 'money' as const, decimals: 2, sortable: false }])]}
						rows={([['today', 'Today', sum.cuts.D0], ['stly', 'STLY', sum.cuts.STLY], ['final', 'Final last year', sum.cuts.LY_ACTUAL]] as const).map(([id, what, f]) => {
							// D40: no last-year data for a night of the window → the last-year rows have no value.
							if (id !== 'today' && sum.lyMissing) return { id, cells: { what, rn: null, rev: null, adr: null, d: null } };
							return { id, cells: { what, rn: f.rn, rev: f.rev, adr: ratio(f.rev, f.rn),
								d: id === 'today' || f.rev === null || f.rev === 0 || sum.cuts.D0.rev === null ? null : sum.cuts.D0.rev / f.rev - 1 } };
						})} />
					<DataTable ariaLabel="Pickup of the window" density={phone ? 'compact' : 'comfortable'} columns={[{ id: 'what', label: 'Pickup', sortable: false }, { id: 'rn', label: 'RN', format: 'number', signed: true, tone: 'change', sortable: false },
						{ id: 'rev', label: 'Revenue', format: 'money', signed: true, tone: 'change', sortable: false }, { id: 'adr', label: 'ADR Δ', format: 'money', decimals: 2, signed: true, tone: 'change', sortable: false }]}
						rows={([1, 3, 7, 14, 30] as const).map(p => {
							const t = sum.cuts.D0, then = sum.cuts[`D${p}`];
							const a = ratio(t.rev, t.rn), b = ratio(then.rev, then.rn);
							return { id: `pu${p}`, cells: { what: `PU ${p}`, rn: t.rn - then.rn, rev: t.rev === null || then.rev === null ? null : t.rev - then.rev, adr: a === null || b === null ? null : a - b } };
						})} />
				</div>
			</ChartCard>}
		</main>
		<Drawer title="Columns" size="small" ref={columnsDrawer.ref}>
			<ColumnsPanel items={items} onChange={onColumns} ariaLabel="Columns of the night table" note="Saved in your account"
				onReset={() => onColumns(panelItems(allColumns, null, defaults))} />
		</Drawer>
	</>;
};

export default BiDailyPerformancePage;
